export interface HotelTimeParts {
  date: string;
  hour: number;
  weekday: number;
}

const FALLBACK_TIMEZONE = 'UTC';
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const formatters = new Map<string, Intl.DateTimeFormat>();

export function resolveHotelTimezone(timezone?: string | null) {
  if (!timezone) {
    return FALLBACK_TIMEZONE;
  }

  try {
    formatterFor(timezone);

    return timezone;
  } catch {
    return FALLBACK_TIMEZONE;
  }
}

export function hotelTimeParts(
  instant: Date | number | string,
  timezone: string
): HotelTimeParts {
  const parts = localParts(new Date(instant), timezone);

  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    hour: Number(parts.hour) % 24,
    weekday: WEEKDAYS.indexOf(parts.weekday),
  };
}

export function hotelMidnight(date: string, timezone: string) {
  const [year, month, day] = date.split('-').map(Number);
  const midnightAsUtc = Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1);
  const firstGuess = midnightAsUtc - utcOffset(midnightAsUtc, timezone);
  const instant = midnightAsUtc - utcOffset(firstGuess, timezone);
  const startsOnDate = hotelTimeParts(instant, timezone).date === date;

  return new Date(startsOnDate ? instant : firstGuess).toISOString();
}

export function addDays(date: string, amount: number) {
  const [year, month, day] = date.split('-').map(Number);

  return new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, (day ?? 1) + amount))
    .toISOString()
    .slice(0, 10);
}

export function daysBetween(startDate: string, endDateExclusive: string) {
  return Math.round(
    (Date.parse(endDateExclusive) - Date.parse(startDate)) / DAY_IN_MS
  );
}

export function mondayOf(date: string) {
  const weekday = (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7;

  return addDays(date, -weekday);
}

function utcOffset(time: number, timezone: string) {
  const parts = localParts(new Date(time), timezone);
  const localTimeAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second)
  );

  return localTimeAsUtc - Math.floor(time / 1000) * 1000;
}

function localParts(date: Date, timezone: string) {
  const parts: Record<string, string> = {};

  formatterFor(timezone)
    .formatToParts(date)
    .forEach(({ type, value }) => {
      parts[type] = value;
    });

  return parts as Record<
    'year' | 'month' | 'day' | 'hour' | 'minute' | 'second' | 'weekday',
    string
  >;
}

function formatterFor(timezone: string) {
  let formatter = formatters.get(timezone);

  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    });
    formatters.set(timezone, formatter);
  }

  return formatter;
}
